import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {onOpenResizeModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {openEditorGridModal} from '@Modals/poster-editor-grid-modal';
import {openEditorTitleModal} from '@Modals/poster-editor-title-modal';
import {openEditorFoldsModal} from '@Modals/poster-editor-folds-modal';
import {getCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import {openPosterEditorBleedModal} from '@Modals/poster-editor-bleed-modal';
import {BackgroundItemFillItem} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/background-item-fill-item';
import {openPosterEditorBackgroundTypeModal} from '@Modals/poster-editor-background-type-modal';
import {openPosterItemsListModal} from '@Modals/poster-items-list-modal';
import {openEditorPosterTools} from '@Components/poster-editor/library/poster-editor-library';
import {onTestButton} from '@PosterWhiteboard/poster/poster-editor-shortcuts';
import {useEditorPrefetchDefaultMobileModules} from '@Hooks/poster-editor/useEditorPrefetchDefaultMobileModules';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {openPosterEditorGuidesModal} from '@Modals/poster-editor-guides-modal';
import {DefaultBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {noop} from '@Utils/general.util';
import {useAppSelector} from '@/hooks';
import {openPageAnimationModal} from '@Modals/poster-editor-page-animation-modal';
import {openPageColorPaletteStylesModel} from '@Modals/poster-editor-page-color-palette-styles-modal/poster-editor-page-color-palette-styles-modal.types';
import {openPosterEditorAiRedesignModal} from '@Components/poster-editor/library/poster-editor-ai-redesign-library';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';

interface DefaultBottomBarProps {
  showSkeleton?: boolean;
}

export function DefaultBottomBar({showSkeleton = false}: DefaultBottomBarProps): ReactElement | null {
  const currentPageObject = useAppSelector(getCurrentPage);
  const bottomBarRef = useRef<HTMLDivElement>(null);
  const teamCredits = useGetTeamCredits();

  useEditorPrefetchDefaultMobileModules();

  const onClickHandler = (itemId: DefaultBottomBarOptions): void => {
    switch (itemId) {
      case DefaultBottomBarOptions.STYLES: {
        trackPosterBuilderGA4Events(GA4EventName.EDITOR_STYLES_VISITED, {[GA4EventParamName.SOURCE]: GA4EventSources.MOBILE_BOTTOM_BAR});
        openPageColorPaletteStylesModel();
        return;
      }
      case DefaultBottomBarOptions.AI_REDESIGN: {
        trackPosterBuilderGA4Events(GA4EventName.AI_REDESIGN_OPENED, {[GA4EventParamName.SOURCE]: GA4EventSources.MOBILE_BOTTOM_BAR});
        openPosterEditorAiRedesignModal(teamCredits);
        return;
      }
      case DefaultBottomBarOptions.RESIZE: {
        onOpenResizeModal();
        return;
      }
      case DefaultBottomBarOptions.BACKGROUND:
        trackPosterBuilderGA4Events(GA4EventName.BACKGROUND_MENU_OPEN, {[GA4EventParamName.SOURCE]: GA4EventSources.MOBILE_BOTTOM_BAR});
        {
          openPosterEditorBackgroundTypeModal();
          return;
        }
      case DefaultBottomBarOptions.ANIMATION: {
        openPageAnimationModal();
        return;
      }
      case DefaultBottomBarOptions.TITLE: {
        openEditorTitleModal();
        return;
      }
      case DefaultBottomBarOptions.GRID: {
        openEditorGridModal();
        return;
      }
      case DefaultBottomBarOptions.FOLDS: {
        openEditorFoldsModal();
        return;
      }
      case DefaultBottomBarOptions.BLEED: {
        openPosterEditorBleedModal();
        return;
      }
      case DefaultBottomBarOptions.ITEMS_LIST: {
        openPosterItemsListModal();
        return;
      }
      case DefaultBottomBarOptions.POSTER_TOOLS: {
        openEditorPosterTools();
        return;
      }
      case DefaultBottomBarOptions.TEST_FUNCTION:
        void onTestButton();
        {
          noop();
          return;
        }
      case DefaultBottomBarOptions.GUIDES: {
        openPosterEditorGuidesModal();
        return;
      }
      default:
        throw new Error('unhandled default bottom bar option type');
    }
  };

  const showSuperUserControls = (): boolean => {
    return window.posterEditor?.config?.showSuperUserOptions || window.PMW.isStorybook();
  };

  const isAnimationOptionHidden = (): boolean => {
    if (!currentPageObject) {
      return false;
    }
    return Object.keys(currentPageObject.items.itemsHashMap).length === 0;
  };

  return (
    <BottomBar
      ref={bottomBarRef}
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      showSkeleton={showSkeleton}
      items={[
        {
          id: DefaultBottomBarOptions.STYLES,
          icon: 'icon-color-palette',
          text: window.i18next.t('pmwjs_styles'),
        },
        {
          id: DefaultBottomBarOptions.AI_REDESIGN,
          icon: 'icon-ai-text',
          text: window.i18next.t('pmwjs_ai_text_update'),
        },
        {
          id: DefaultBottomBarOptions.RESIZE,
          icon: 'icon-resize',
          text: window.i18next.t('pmwjs_resize'),
        },
        {
          id: DefaultBottomBarOptions.BACKGROUND,
          customContent: <BackgroundItemFillItem />,
          text: window.i18next.t('pmwjs_background'),
        },
        {
          id: DefaultBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
          isHidden: isAnimationOptionHidden(),
        },
        {
          id: DefaultBottomBarOptions.TITLE,
          icon: 'icon-title',
          text: window.i18next.t('pmwjs_title'),
        },
        {
          id: DefaultBottomBarOptions.GRID,
          icon: 'icon-grid',
          text: window.i18next.t('pmwjs_grid'),
        },
        {
          id: DefaultBottomBarOptions.FOLDS,
          icon: 'icon-folds',
          text: window.i18next.t('pmwjs_folds'),
        },
        {
          id: DefaultBottomBarOptions.BLEED,
          icon: 'icon-bleed',
          text: window.i18next.t('pmwjs_bleed'),
        },
        {
          id: DefaultBottomBarOptions.GUIDES,
          icon: 'icon-guides',
          text: window.i18next.t('pmwjs_guides'),
        },
        {
          id: DefaultBottomBarOptions.ITEMS_LIST,
          icon: 'icon-templates-stack',
          text: window.i18next.t('pmwjs_items_list'),
          isHidden: !showSuperUserControls(),
        },
        {
          id: DefaultBottomBarOptions.POSTER_TOOLS,
          icon: 'icon-info',
          text: window.i18next.t('pmwjs_poster_tools'),
          isHidden: !showSuperUserControls(),
        },
        {
          id: DefaultBottomBarOptions.TEST_FUNCTION,
          icon: 'icon-screen-rocket',
          text: window.i18next.t('pmwjs_test'),
          isHidden: !showSuperUserControls(),
        },
      ]}
    />
  );
}
